import React, { isValidElement } from 'react';
import classNames from 'classnames';
import { TextField as AriaTextField, Label, Input, Text } from 'react-aria-components';
import PropTypes from 'prop-types';
import AdvancedDescription from '../AdvancedDescription';
import Notice from '../../misc/Notice';
import Group from '../Group';
import Button from '../../buttons/Button';
import AsLinkButton from '../../buttons/AsLinkButton';

/**
 * A component for entering short format text
 */
function TextField({
  id,
  size = 'medium',
  label,
  description,
  descriptionAfter,
  advancedDescription,
  advancedDescriptionButtonText,
  prefix,
  suffix,
  errors,
  isRequired,
  isHidden,
  hasResults,
  isLabelHidden,
  className,
  groupElement,
  ...props
}) {
  const textFieldClasses = classNames('group__element', className, {
    'group__element--has-prefix-suffix': prefix || suffix,
    'suffix--has-button': isValidElement(suffix) && (suffix.type === Button || suffix.type === AsLinkButton)
  });

  if (advancedDescription) {
    advancedDescription = <Text slot="description">{advancedDescription}</Text>;
  } else {
    if (description) {
      description = <Text slot="description">{description}</Text>;
    }
  }

  return (
    <Group
      id={id}
      type="textfield"
      size={size}
      isRequired={isRequired}
      isHidden={isHidden}
      className={classNames({ ['form__textfield--has-results']: hasResults, ['no-label']: isLabelHidden })}
    >
      <AriaTextField {...props}>
        <Label className="group__label">{label}</Label>
        {description && <div className="group__description">{description}</div>}
        <div className={textFieldClasses}>
          {prefix && <div className="group__prefix">{prefix}</div>}
          <Input data-cw-id={id} />
          {suffix && <div className="group__suffix">{suffix}</div>}
          {groupElement}
        </div>
        {descriptionAfter && <div className="group__lower-description">{descriptionAfter}</div>}
        {advancedDescription && (
          <AdvancedDescription buttonText={advancedDescriptionButtonText} text={advancedDescription} />
        )}

        {(errors || (props.maxLength && props.maxLengthMessage && props.value.length >= props.maxLength)) && (
          <div className="group__errors">
            {props.maxLength && props.maxLengthMessage && props.value.length >= props.maxLength && (
              <Notice type={'warning-alert'}>
                <Text slot="errorMessage">{props.maxLengthMessage}</Text>
              </Notice>
            )}

            {errors &&
              errors.map((error, index) => {
                return (
                  <Notice key={index} type={'warning-alert'}>
                    {index === 0 ? <Text slot="errorMessage">{error}</Text> : error}
                  </Notice>
                );
              })}
          </div>
        )}
      </AriaTextField>
    </Group>
  );
}

TextField.propTypes = {
  /**
   * The id
   */
  id: PropTypes.string,
  /**
   * The field size
   */
  size: PropTypes.string,
  /**
   * The label
   */
  label: PropTypes.string.isRequired,
  /**
   * The classes
   */
  className: PropTypes.string || PropTypes.arrayOf(PropTypes.string),
  /**
   * The description
   */
  description: PropTypes.string,
  /**
   * The description after
   */
  descriptionAfter: PropTypes.string,
  /**
   * The prefix
   */
  prefix: PropTypes.node,
  /**
   * The suffix
   */
  suffix: PropTypes.node,
  /**
   * The advanced description
   */
  advancedDescription: PropTypes.string,
  /**
   * The advanced description button text
   */
  advancedDescriptionButtonText: PropTypes.string,
  /**
   * The errors
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * The label hidden flag
   */
  isLabelHidden: PropTypes.bool,
  /**
   * The required flag
   */
  isRequired: PropTypes.bool,
  /**
   * The hidden flag
   */
  isHidden: PropTypes.bool,
  /**
   * The has results flag
   */
  hasResults: PropTypes.bool,
  /**
   * The group element
   */
  groupElement: PropTypes.node,
  /**
   * The nested children
   */
  children: PropTypes.node,
  /**
   * The change handler
   */
  onChange: PropTypes.func
};

export default TextField;
